import {
  DEFAULT_SUPPORT_PORTAL_URL,
  HIDDEN_PATH_PREFIXES,
  JSD_WIDGET_BASE_URL,
  JSD_WIDGET_IFRAME_ID,
  JSD_WIDGET_SCRIPT_ID,
  JSD_WIDGET_SCRIPT_SRC,
} from './constants'

export const getSupportPortalUrl = () =>
  String(
    process.env.REACT_APP_JSM_PORTAL_URL || DEFAULT_SUPPORT_PORTAL_URL
  ).trim()

export const getJsdWidgetKey = () =>
  String(process.env.REACT_APP_JSM_WIDGET_KEY || '').trim()

export const isSupportWidgetEnabled = () => {
  const flag = String(process.env.REACT_APP_ENABLE_SUPPORT_WIDGET || 'true')
    .trim()
    .toLowerCase()
  return flag !== 'false' && flag !== '0'
}

export const shouldHideSupportWidget = (pathname = '') => {
  const path = pathname.split('?')[0]
  return HIDDEN_PATH_PREFIXES.some(
    (prefix) => path === prefix || path.startsWith(`${prefix}/`)
  )
}

export const isEditableTarget = (target) => {
  if (!target || target === document.body) return false
  if (target.isContentEditable) return true
  const tag = String(target.tagName || '').toUpperCase()
  return tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT'
}

export const isHelpShortcut = (event) => {
  if (event.altKey || event.ctrlKey || event.metaKey) return false
  if (event.key === '?') return true
  return event.key === '/' && event.shiftKey
}

export const isIframeEmbedBlocked = (iframe) => {
  if (!iframe) return true
  try {
    const href = iframe.contentWindow?.location?.href
    if (!href || href === 'about:blank') return true
    if (href.startsWith('chrome-error:') || href.startsWith('chrome://')) {
      return true
    }
    return false
  } catch (_error) {
    // A SecurityError means a cross-origin document loaded, so embedding worked.
    return false
  }
}

export const openSupportPortal = (url = getSupportPortalUrl()) => {
  window.open(url, '_blank', 'noopener,noreferrer')
}

export const isJsdWidgetMounted = () =>
  typeof document !== 'undefined' &&
  Boolean(document.getElementById(JSD_WIDGET_IFRAME_ID))

export const mountJsdWidget = (widgetKey) => {
  if (!widgetKey || typeof document === 'undefined') return null

  const existing = document.getElementById(JSD_WIDGET_SCRIPT_ID)
  if (existing) return existing

  const script = document.createElement('script')
  script.id = JSD_WIDGET_SCRIPT_ID
  script.async = true
  script.setAttribute('data-jsd-embedded', '')
  script.setAttribute('data-key', widgetKey)
  script.setAttribute('data-base-url', JSD_WIDGET_BASE_URL)
  script.src = JSD_WIDGET_SCRIPT_SRC
  document.body.appendChild(script)
  return script
}

// embed.js only boots on DOMContentLoaded. React injects it after that event
// has already fired, so replay it once the script has executed.
export const startJsdWidget = () => {
  if (typeof document === 'undefined') return false
  if (isJsdWidgetMounted()) return true
  if (!document.querySelector('script[data-jsd-embedded]')) return false
  if (document.readyState === 'loading') return false
  document.dispatchEvent(new Event('DOMContentLoaded'))
  return isJsdWidgetMounted()
}

export const unmountJsdWidget = () => {
  if (typeof document === 'undefined') return
  document.getElementById(JSD_WIDGET_SCRIPT_ID)?.remove()
  document.getElementById(JSD_WIDGET_IFRAME_ID)?.remove()
  document.getElementById('jsd-widget-description')?.remove()
}

// The official launcher is a 50px circle inside the iframe, inset from the
// iframe's bottom-right by the widget's own padding and measurement slack.
export const JSD_LAUNCHER_BUTTON_SIZE = 50
export const JSD_LAUNCHER_RIGHT_INSET = 10
export const JSD_LAUNCHER_BOTTOM_INSET = 20
export const JSD_LAUNCHER_SCALE = 0.8
export const JSD_COLLAPSED_MAX_SIZE = 120

export const isCollapsedJsdLauncher = (width, height) =>
  width > 0 &&
  height > 0 &&
  width <= JSD_COLLAPSED_MAX_SIZE &&
  height <= JSD_COLLAPSED_MAX_SIZE

export const getLauncherTintBox = (rect, scale = JSD_LAUNCHER_SCALE) => {
  const size = JSD_LAUNCHER_BUTTON_SIZE * scale
  const rightInset = JSD_LAUNCHER_RIGHT_INSET * scale
  const bottomInset = JSD_LAUNCHER_BOTTOM_INSET * scale
  return {
    width: size,
    height: size,
    fontSize: Math.round(29 * scale),
    left: rect.right - rightInset - size,
    top: rect.bottom - bottomInset - size,
  }
}
